import { Component, Inject, OnInit, Optional, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { first, Observable, of, Subject } from 'rxjs';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { debounceTime, delay, map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { DataSourceService } from '../../../../_core/state/data-source/data-source.service';
import {
	DataSourceConnectorType,
	DataSourceContext,
	ItemFormat,
	PublicDataSource
} from '../../../../_core/state/data-source/data-sources.data';
import { validJsonValidator } from '../../../../_core/utils/validation.utils';
import { QueryAnimationDialogComponent } from '../../../../shared/components/query-animation-dialog/query-animation-dialog.component';
import { stringToSlug } from '../../../../shared/utils/commonUtil';
import { GlobalQuery, GlobalService } from '../../../../_core/state/global';
import { GlobalSettings } from '../../../../_core/state/global/global.model';
import { FilterDef, FilterLayout, FilterType } from '../../../../../../../api/src/data-source/models/filter.model';
import { getFilterTypeTitle } from '../../../../_core/state/filter/filter.utils';
import { take } from 'rxjs/operators';
import { ConnectorConfigBria } from '../../../../../../../api/src/data-source/connectors/bria/models';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { stringToFilename } from '../../../../_core/utils/string.utils';
import { DataImportDialogDataModel, ImportEvent } from '../../../../shared/components/data-import-dialog/data-import-dialog-data.model';
import { DataImportDialogComponent } from '../../../../shared/components/data-import-dialog/data-import-dialog.component';
import { ResponseShowcaseComponent } from '../../admin-data-import/response-showcase/response-showcase.component';
import { DOCUMENT } from '@angular/common';
import { Business, BusinessQuery, BusinessService } from '../../../../_core/state/business';
import { SessionQuery } from '../../../../_core/state/session';

export interface AdminDataSourceDialogData {
	dataSource?: PublicDataSource;
}

@Component({
	selector: 'app-admin-data-source-dialog',
	templateUrl: './admin-data-source-dialog.component.html',
	styleUrls: ['./admin-data-source-dialog.component.scss']
})
export class AdminDataSourceDialogComponent implements OnInit, OnDestroy {
	readonly ItemFormat = ItemFormat;
	readonly DataSourceConnectorType = DataSourceConnectorType;
	readonly DataSourceContext = DataSourceContext;
	readonly FilterType = FilterType;
	readonly FilterLayout = FilterLayout;
	readonly getFilterTypeTitle = getFilterTypeTitle;

	public form: UntypedFormGroup;
	public currentTabIndex = 0;
	public codeCache: PublicDataSource;
	public formCache: PublicDataSource;
	public businesses$: Observable<Business[]>;
	public siteCopy$: Observable<Partial<GlobalSettings>>;
	public adminMode$: Observable<boolean>;
	public selectedBusinessIds: string[] = [];
	public businessOptions: { value: string; label: string }[] = [];
	public isSuperAdmin: boolean = false;

	// Inline embedding API
	@Input() inline = false;
	@Input() inlineDataSource?: PublicDataSource;
	@Input() showCloseButton = true;
	@Output() close = new EventEmitter<any>();

	private _unsubscribe: Subject<void> = new Subject();

	/**
	 * Get the current data source being edited
	 * Works for both dialog mode (uses data.dataSource) and inline mode (uses inlineDataSource)
	 */
	get currentDataSource(): PublicDataSource | undefined {
		return this.data?.dataSource || this.inlineDataSource;
	}

	constructor(
		private readonly dataSourceService: DataSourceService,
		private readonly formBuilder: UntypedFormBuilder,
		private readonly dialog: MatDialog,
		@Optional() public dialogRef: MatDialogRef<AdminDataSourceDialogComponent>,
		public readonly globalService: GlobalService,
		private readonly globalQuery: GlobalQuery,
		private readonly businessQuery: BusinessQuery,
		private readonly businessService: BusinessService,
		private readonly sessionQuery: SessionQuery,
		@Optional() @Inject(MAT_DIALOG_DATA) public data: AdminDataSourceDialogData,
		@Inject(DOCUMENT) private document: Document
	) {
		this._createForm();

		// Note: In inline mode, data from @Input properties is not yet available in constructor
		// We'll handle inline data in ngOnInit where @Input is available
		
		// When used in a dialog, MAT_DIALOG_DATA will be available
		if (this.data?.dataSource) {
			this._setFormData(this.data.dataSource);
			// Initialize selectedBusinessIds from existing data source
			this.selectedBusinessIds = this.data.dataSource.businesses?.map(b => b.id) || [];
		}

		// Check if user is a super admin (based on their role, not UI toggle)
		const isSuperAdmin = this.sessionQuery.isAtLeastSuperAdmin();
		this.adminMode$ = of(isSuperAdmin);
		
		// Get businesses based on user role:
		// - Super admins see all businesses (after loading them)
		// - Regular users see only their associated businesses
		this.businesses$ = of(isSuperAdmin).pipe(
			take(1),
			// First, load all businesses if super admin
			switchMap(isAdmin => {
				if (isAdmin) {
					// Load all businesses and wait for completion
					return this.businessService.loadAllBusinesses().pipe(
						// After loading, emit the admin status
						map(() => isAdmin),
						// Small delay to ensure store is updated
						delay(100)
					);
				}
				return of(isAdmin);
			}),
			// Then, get the filtered business list from the store
			switchMap(isAdmin => {
				// Get fresh data from the store after loading
				const businesses = this.businessQuery.getAll();
				return of([businesses, isAdmin] as [Business[], boolean]);
			}),
			map(([allBusinesses, isAdmin]) => {
				if (isAdmin) {
					return allBusinesses;
				}
				// Get user's associated business IDs from session
				const userBusinessIds = this.businessQuery.getUserBusinesses()?.map(b => b.id) || [];
				// Filter to only show businesses the user is associated with
				return allBusinesses.filter(b => userBusinessIds.includes(b.id));
			})
		);
		
		// Get site copy for business label
		this.siteCopy$ = this.globalQuery.copy$;
	}

	ngOnInit(): void {
		// When embedded inline, @Input properties are now available
		// Priority: inline mode uses inlineDataSource, dialog mode uses data.dataSource
		if (this.inline && this.inlineDataSource) {
			this._setFormData(this.inlineDataSource);
			this.selectedBusinessIds = this.inlineDataSource.businesses?.map(b => b.id) || [];
		} else if (!this.data?.dataSource && this.inlineDataSource) {
			// Fallback: if data.dataSource is not set but inlineDataSource is provided
			this._setFormData(this.inlineDataSource);
			this.selectedBusinessIds = this.inlineDataSource.businesses?.map(b => b.id) || [];
		}
		
		// Set isSuperAdmin flag for template use
		this.isSuperAdmin = this.sessionQuery.isAtLeastSuperAdmin();
		
		// Load business options and initialize selected businesses
		this.businessService
			.findBusinessesForPackage()
			.pipe(take(1))
			.subscribe(data => {
				this.businessOptions = data;
				
				// Initialize selected businesses from existing data source after business options are loaded
				if (this.data?.dataSource?.businesses?.length) {
					this.selectedBusinessIds = this.data.dataSource.businesses.map(b => b.id);
					console.log('Loaded business associations for data source:', {
						dataSourceName: this.data.dataSource.name,
						businessIds: this.selectedBusinessIds,
						availableBusinessOptions: data.length
					});
				}
				// Auto-select business if user is creating a new data source and belongs to only one business
				else if (!this.data?.dataSource && !this.inlineDataSource && data.length === 1 && this.selectedBusinessIds.length === 0) {
					this.selectedBusinessIds = [data[0].value];
					console.log('Auto-selected single business for new data source:', data[0].label);
				}
			});
		
		//this.startSlugValidation();
	}

	ngOnDestroy(): void {
		this._unsubscribe.next();
		this._unsubscribe.complete();
	}

	onBusinessSelectionChange(newSelection: string[]) {
		// Non-super admins cannot remove all business associations when editing
		if (!this.isSuperAdmin && newSelection.length === 0 && this.data?.dataSource) {
			this.globalService.triggerErrorMessage(
				undefined,
				$localize`:Error Message@@dataSourceCannotRemoveAllBusinesses:Cannot remove all business associations. Data source must be associated with at least one business.`
			);
			// Don't update the selection
			return;
		}
		
		this.selectedBusinessIds = newSelection;
	}

	get mapping(): UntypedFormArray {
		return (this.form.controls['config'] as UntypedFormGroup)?.controls['mapping'] as UntypedFormArray;
	}

	get context(): UntypedFormArray {
		return (this.form.controls['config'] as UntypedFormGroup)?.controls['context'] as UntypedFormArray;
	}

	get sortableFields(): UntypedFormArray {
		return (this.form.controls['config'] as UntypedFormGroup)?.controls['sortableFields'] as UntypedFormArray;
	}

	get filters(): UntypedFormArray {
		return (this.form.controls['config'] as UntypedFormGroup)?.controls['filters'] as UntypedFormArray;
	}

	async submit() {
		const formValue = this.form.value;

		// Validate selectedBusinessIds for non-super admins
		// Non-super admins can only use businesses they belong to
		const isSuperAdmin = this.sessionQuery.isAtLeastSuperAdmin();
		if (!isSuperAdmin) {
			// Non-super admins cannot remove all business associations
			if (this.selectedBusinessIds.length === 0) {
				this.globalService.triggerErrorMessage(
					undefined,
					$localize`:Error Message@@dataSourceCannotRemoveAllBusinesses:Cannot remove all business associations. Data source must be associated with at least one business.`
				);
				return;
			}

			const userBusinessIds = this.businessQuery.getUserBusinesses()?.map(b => b.id) || [];
			// Filter to only include businesses the user has access to
			this.selectedBusinessIds = this.selectedBusinessIds.filter(id => userBusinessIds.includes(id));
		}
		// Super admins can set any businesses, including an empty array to remove all associations

		//fix config

		switch (formValue.connectorType) {
			case DataSourceConnectorType.CSV:
				formValue.config.environment = undefined;
				formValue.config.apiKey = undefined;
				formValue.config.userEmail = undefined; // Remove user email field
				formValue.config.accessToken = undefined;
				break;

			case DataSourceConnectorType.SmartSheet:
				formValue.config.environment = undefined;
				formValue.config.apiKey = undefined;
				formValue.config.columnSeparator = undefined;
				formValue.dataSourceLink = this.form.get('config.sourceLink').value || '';
				formValue.config.userEmail = undefined; // Remove user email field

				break;

			case DataSourceConnectorType.Cloudinary:
				formValue.config.columnSeparator = undefined;
				formValue.config.apiKey = undefined;
				formValue.config.userEmail = undefined; // Remove user email field
				break;

			case DataSourceConnectorType.Strapi:
				formValue.config.environment = undefined;
				formValue.config.accessToken = undefined;
				formValue.config.apiKey = undefined;
				formValue.config.columnSeparator = undefined;
				formValue.config.userEmail = undefined; // Remove user email field
				break;

			case DataSourceConnectorType.Pexels:
				formValue.config.environment = undefined;
				formValue.config.accessToken = undefined;
				formValue.config.apiKey = undefined;
				formValue.config.columnSeparator = undefined;
				formValue.config.userEmail = undefined; // Remove user email field

				break;

			case DataSourceConnectorType.Bria:
				formValue.config.environment = undefined;
				formValue.config.accessToken = undefined;
				formValue.config.apiKey = undefined;
				formValue.config.columnSeparator = undefined;
				formValue.config.userEmail = undefined; // Remove user email field

				break;

			case DataSourceConnectorType.Brandshare:
				formValue.config.environment = undefined;
				formValue.config.accessToken = undefined;
				formValue.config.columnSeparator = undefined;
				formValue.config.userEmail = undefined; // Remove user email field
				break;

			case DataSourceConnectorType.Box:
				formValue.config.environment = undefined;
				formValue.config.columnSeparator = undefined;
				formValue.config.userEmail = undefined;
				formValue.config.apiKey = undefined; // Remove apiKey field that Box doesn't use
				
				// Convert empty strings to undefined for Box-specific fields
				if (formValue.config.baseUrl === '') formValue.config.baseUrl = undefined;
				if (formValue.config.clientId === '') formValue.config.clientId = undefined;
				if (formValue.config.accessToken === '') formValue.config.accessToken = undefined;
				if (formValue.config.enterpriseId === '') formValue.config.enterpriseId = undefined;
				break;

      case DataSourceConnectorType.Native:
				formValue.config.environment = undefined;
				formValue.config.accessToken = undefined;
				formValue.config.apiKey = undefined;
				formValue.config.columnSeparator = undefined;
				formValue.config.userEmail = undefined; // Remove user email field
				break;
		}

		// fix context
		if (formValue.config.context?.length) {
			formValue.config.context = formValue.config.context
				.filter((c: any) => c !== null && c !== undefined) // Filter out null/undefined values
				.map((c: any) => c.context || c); // Handle both nested and direct context values
		} else {
			formValue.config.context = undefined; // Remove empty context arrays
		}

		// fix params
		if (formValue.config.params) {
			formValue.config.params =
				typeof formValue.config.params === 'object' ? formValue.config.params : JSON.parse(formValue.config.params);
		} else {
			formValue.config.params = undefined;
		}

		delete formValue.slug;
		
		// Set businessIds from selectedBusinessIds
		formValue.businessIds = this.selectedBusinessIds;

		let query: Observable<any>;

		if (this.currentDataSource) {
			query = this.dataSourceService.update(this.currentDataSource.slug, formValue);
		} else {
			query = this.dataSourceService.create(formValue);
		}

		this.dialogRef?.addPanelClass('hidden');
		this.dialog
			.open(QueryAnimationDialogComponent, {
				width: '640px',
				panelClass: ['background-color-alternative', 'max-width-lg'],
				disableClose: true,
				data: {
					title: $localize`:Dialog Title@@adminDataSourceAddTitle:Add Data Source`,
					showClose: 'auto',
					progressMessage: 'Verifying the configuration ... ',
					successMessage: `Data source “${formValue.name}” successfully ${this.data?.dataSource ? 'updated' : 'added'}`,
					failureMessage: response =>
						`Verification failed, data source not added there seems to be a problem with ${
							response?.error ? response.error.message : response?.message
						}`,
					query: query,
					backButtonText: 'Back to configuration'
				}
			})
			.afterClosed()
			.pipe(first())
			.subscribe((response: any | 'discard' | 'back') => {
				if (response === 'discard') {
					if (this.dialogRef) { this.dialogRef.close(); } else { this.close.emit(); }
					return;
				}

				if (response === 'back') {
					this.dialogRef?.removePanelClass('hidden');
					return;
				}

				if (this.dialogRef) {
					return this.dialogRef.close(response);
				}
				return this.close.emit(response);
			});
	}

	generateMappings() {
		this.dataSourceService.query(this.form.value.slug, {}).subscribe(
			response => {
				// Build an object of the schema of the fist item within results.  Recursively go through the object and add arrays with a dot notated path.
				const schema = Object.keys(response.data.results[0]).reduce((acc, key) => {
					const value = response.data.results[0][key];

					// Get the first item of the array and return a schema of the object.
					if (Array.isArray(value)) {
						const arraySchema = value.reduce((arrayAcc, item, index) => {
							if (index === 0) {
								const itemSchema = Object.keys(item).reduce((itemAcc, itemKey) => {
									itemAcc[`${key}.[].${itemKey}`] = parseInt(item[itemKey]) ? ItemFormat.Number : ItemFormat.Text;
									return itemAcc;
								}, {});
								return { ...arrayAcc, ...(itemSchema || {}) };
							} else {
								return arrayAcc;
							}
						}, {});

						return { ...acc, ...arraySchema };

						// Iterate through the object and return a schema of the object.
					} else if (typeof value === 'object' && value !== null) {
						const objectSchema = Object.keys(value).reduce((objectAcc, objectKey) => {
							objectAcc[`${key}.${objectKey}`] = parseInt(value) ? ItemFormat.Number : ItemFormat.Text;
							return objectAcc;
						}, {});
						return { ...acc, ...objectSchema };
					} else {
						// If the value is a string or number, add it to the schema.
						switch (typeof value) {
							case 'string':
								acc[key] = ItemFormat.Text;
								break;
							case 'number':
								acc[key] = ItemFormat.Number;
								break;
							case 'boolean':
								acc[key] = ItemFormat.Other;
								break;
							default:
								acc[key] = ItemFormat.Text;
								break;
						}
					}
					return acc;
				}, {});

				// Convert into an array of mappings
				const mappings = Object.keys(schema).map(key => {
					return {
						path: key,
						displayName: key,
						format: schema[key]
					};
				});

				// Reset form array to equal the mappings
				this.mapping.clear();
				mappings.forEach(mapping => {
					this.mapping.push(this._createMapping(mapping));
				});
			},
			error => this.globalService.triggerErrorMessage(error)
		);
	}

	addMapping() {
		if (!this.mapping) {
			this.form.patchValue({ config: { mapping: [] } });
		}

		this.mapping?.push(this._createMapping({ path: '', displayName: '', format: '' }));
	}

	addContext() {
		if (!this.context) {
			this.form.patchValue({ config: { context: [] } });
		}

		this.context?.push(this._createContext({ context: '' }));
	}

	addSortableField() {
		if (!this.sortableFields) {
			this.form.patchValue({ config: { sortableFields: [] } });
		}

		this.sortableFields?.push(this._createSortableField({ path: '', displayName: '' }));
	}

	addFilter() {
		if (!this.filters) {
			this.form.patchValue({ config: { filters: [] } });
		}

		this.filters?.push(
			this._createFilter({
				id: '',
				name: '',
				category: 'overall',
				paramKey: '',
				type: FilterType.Search,
				layout: FilterLayout.Horizontal,
				grow: true,
				topPadding: true
			})
		);
	}

	removeMapping(index: number) {
		this.mapping.removeAt(index);
	}

	removeContext(index: number) {
		this.context.removeAt(index);
	}

	removeSortableField(index: number) {
		this.sortableFields.removeAt(index);
	}

	removeFilter(index: number) {
		this.filters.removeAt(index);
	}

	public copyToClipboard() {
		const smartsheetUrl = this.form.get('config.sourceLink').value;
		this.globalService.copyToClipboard(smartsheetUrl);
	}

	onChangeTab(tab: MatTabChangeEvent) {
		this.currentTabIndex = tab.index;
		if (tab.index === 0) {
			//if we don't change anything in monaco editor codeCache is undefined
			if (this.codeCache) {
				this._setFormData(this.codeCache);
			}
			this.codeCache = undefined;
		}
	}

	onSetCodeCache(codeCache: PublicDataSource) {
		this.codeCache = codeCache;
		this._setFormData(codeCache);
		this.form.markAllAsTouched();
	}

	private _createForm() {
		this.form = this.formBuilder.group({
			name: [this.data?.dataSource?.name, [Validators.required]],
			slug: { value: this.data?.dataSource?.slug, disabled: true },
			connectorType: [this.data?.dataSource?.connectorType || Object.values(DataSourceConnectorType)?.[0], [Validators.required]],
			locale: [this.data?.dataSource?.locale],
			public: [this.data?.dataSource?.public || false],
			config: this.formBuilder.group({
				target: [this.data?.dataSource?.config?.target, [Validators.required]],
				accessToken: [this.data?.dataSource?.config?.accessToken || null],
				apiKey: [undefined],
				userEmail: [''],
				columnSeparator: [''],
				environment: [''],
				// Box-specific fields
				clientId: [this.data?.dataSource?.config?.clientId || ''],
				baseUrl: [this.data?.dataSource?.config?.baseUrl || ''],
				enterpriseId: [this.data?.dataSource?.config?.enterpriseId || ''],
				saveToLibrary: [this.data?.dataSource?.config?.saveToLibrary ?? this.data?.dataSource?.connectorType === DataSourceConnectorType.Box],
				// AssetCollection-specific fields
				assetPathProperty: [this.data?.dataSource?.config?.assetPathProperty || ''],
				assetTitleProperty: [this.data?.dataSource?.config?.assetTitleProperty || ''],
				context: this.formBuilder.array([]),
				params: ['', validJsonValidator(true)],
				mapping: this.formBuilder.array([]),
				sortableFields: this.formBuilder.array([]),
				filters: this.formBuilder.array([]),
				previewPath: [this.data?.dataSource?.config?.previewPath],
				tagPath: [this.data?.dataSource?.config?.tagPath],
				sourceLink: [{ value: this.data?.dataSource?.dataSourceLink || '', disabled: true }]
			})
		});

		// backwards compatibility for old data sources that do not have the sourceLink set yet
		const isSmartsheetType: boolean = this.data?.dataSource?.connectorType === DataSourceConnectorType.SmartSheet;
		const noDatasourceLink: boolean = !this.data?.dataSource?.dataSourceLink || this.data?.dataSource?.dataSourceLink === '';
		if (isSmartsheetType && noDatasourceLink) {
			this._setSmartSheetUrl();
		}

		// // Add specific controls based on the connector type here.
		// switch(this.form.get('connectorType').value) {
		// 	case DataSourceConnectorType.Bria:
		// 		// Add a models control inside of the config group.
		// 		let models = this.data?.dataSource?.config?.models;
		// 		console.log('models', this.data?.dataSource);
		// 		if (!models) {
		// 			let briaConfig = new ConnectorConfigBria();
		// 			models = briaConfig.models;
		// 		}

		// 		(this.form.get('config') as UntypedFormGroup).addControl('models', this.formBuilder.control(models));
		// 		break;
		// }

		this.form.valueChanges.pipe(takeUntil(this._unsubscribe)).subscribe(value => {
			if (this.currentTabIndex === 0) {
				this.formCache = {
					...value,
					config: {
						...value.config,
						context: value.config?.context?.map((c: any) => c.context) || [],
						params: value.config?.params ? JSON.parse(value.config.params) : undefined
					}
				};
			}

			// If the context contains and image, video, or audio picker, then add a new control for the assetPath and name.
			if (this.context?.value?.length) {
				const context = this.context.value;
				let match;
				context.forEach((c: any) => {
					if (
						c.context === DataSourceContext.ImagePickerLink ||
						c.context === DataSourceContext.VideoPickerLink ||
						c.context === DataSourceContext.AudioPickerLink
					) {
						match = true;
					}
				});
				if (match) {
					if (!this.form.get('config.assetPathProperty')) {
						console.log('assetPath', this.data?.dataSource?.config?.assetPathProperty);
						(this.form.get('config') as UntypedFormGroup).addControl(
							'assetPathProperty',
							this.formBuilder.control(this.data?.dataSource?.config?.assetPathProperty)
						);
					}

					if (!this.form.get('config.assetTitleProperty')) {
						(this.form.get('config') as UntypedFormGroup).addControl(
							'assetTitleProperty',
							this.formBuilder.control(this.data?.dataSource?.config?.assetTitleProperty)
						);
					}
				} else {
					if (this.form.get('config.assetPathProperty')) {
						(this.form.get('config') as UntypedFormGroup).removeControl('assetPathProperty');
					}

					if (this.form.get('config.assetTitleProperty')) {
						(this.form.get('config') as UntypedFormGroup).removeControl('assetTitleProperty');
					}
				}
			}
		});

		this.form
			.get('connectorType')
			.valueChanges.pipe(takeUntil(this._unsubscribe), startWith(this.form.get('connectorType').value))
			.subscribe(connectorType => {
				// setup validators for connector type
				switch (connectorType) {
					case DataSourceConnectorType.CSV:
					case DataSourceConnectorType.Cloudinary:
						
					case DataSourceConnectorType.Strapi:
						this.form.get('config.target')?.setValidators([Validators.required]);
						this.form.get('config.accessToken')?.clearValidators();
						break;
					case DataSourceConnectorType.SmartSheet:
						this.form.get('config.target')?.setValidators([Validators.required]);
						this.form.get('config.userEmail')?.clearValidators();
						if (this.currentDataSource) {
							this.form.get('config.accessToken')?.clearValidators();
						} else {
							this.form.get('config.accessToken')?.setValidators([Validators.required]);
						}
						break;
					case DataSourceConnectorType.Strapi:
						this.form.get('config.accessToken')?.removeValidators([Validators.required]);
						this.form.get('config.accessToken')?.updateValueAndValidity();
						break;

					case DataSourceConnectorType.Bria:
					case DataSourceConnectorType.Pexels:
						this.form.get('config.target')?.removeValidators([Validators.required]);
						break;

					case DataSourceConnectorType.Brandshare:
						this.form.get('config.target')?.setValidators([Validators.required]);
						this.form.get('config.apiKey')?.clearValidators();
						this.form.get('config.userEmail')?.clearValidators();
						this.form.get('config.accessToken')?.clearValidators();
						break;

				case DataSourceConnectorType.Box:
					this.form.get('config.target')?.clearValidators(); // Folder ID is optional
					this.form.get('config.target')?.updateValueAndValidity();
					this.form.get('config.clientId')?.clearValidators(); // Optional for JWT auth
					this.form.get('config.clientId')?.updateValueAndValidity();
					this.form.get('config.accessToken')?.clearValidators(); // Optional for JWT auth
					this.form.get('config.accessToken')?.updateValueAndValidity();
					this.form.get('config.baseUrl')?.clearValidators();
					this.form.get('config.baseUrl')?.updateValueAndValidity();
					this.form.get('config.enterpriseId')?.clearValidators();
					this.form.get('config.enterpriseId')?.updateValueAndValidity();
					break;

				case DataSourceConnectorType.AssetCollection:
					this.form.get('config.target')?.setValidators([Validators.required]); // Collection ID is required
					this.form.get('config.target')?.updateValueAndValidity();
					this.form.get('config.assetPathProperty')?.clearValidators(); // Optional
					this.form.get('config.assetPathProperty')?.updateValueAndValidity();
					this.form.get('config.assetTitleProperty')?.clearValidators(); // Optional
					this.form.get('config.assetTitleProperty')?.updateValueAndValidity();
					this.form.get('config.accessToken')?.clearValidators();
					this.form.get('config.accessToken')?.updateValueAndValidity();
					break;

				case DataSourceConnectorType.Native:
						this.form.get('config.accessToken')?.clearValidators();
						this.form.get('config.target')?.clearValidators();
						this.form.get('config.apiKey')?.clearValidators();
						this.form.get('config.userEmail')?.clearValidators();
						break;
            
					default:
						this.form.get('config.apiKey')?.clearValidators();
						this.form.get('config.userEmail')?.clearValidators();
						break;
				}

				/*
					Add / Remove specific controls based on the connector type here.
				*/
				if (connectorType === DataSourceConnectorType.Bria) {
					if (!this.form.get('config.models')) {
						// Add a models control inside of the config group.
						let models = this.data?.dataSource?.config?.models;
						console.log('models', this.data?.dataSource);
						if (!models) {
							let briaConfig = new ConnectorConfigBria();
							models = briaConfig.models;
						}

						(this.form.get('config') as UntypedFormGroup).addControl('models', this.formBuilder.control(models));
					}

					if (!this.form.get('config.useBriaIframe')) {
						(this.form.get('config') as UntypedFormGroup).addControl(
							'useBriaIframe',
							this.formBuilder.control(this.data?.dataSource?.config?.useBriaIframe || false)
						);
					}
				} else if (this.form.get('config.models')) {
					(this.form.get('config') as UntypedFormGroup).removeControl('models');
					(this.form.get('config') as UntypedFormGroup).removeControl('useBriaIframe');
				}

				this.form.get('config.accessToken').updateValueAndValidity();
				this.form.get('config.target').updateValueAndValidity();
				this.form.get('config.apiKey').updateValueAndValidity();
				this.form.get('config.userEmail').updateValueAndValidity();
				this.form.updateValueAndValidity();
			});

		// handle the smartsheet connection changes
		const accessToken = this.form.get('config.accessToken').valueChanges;
		const target = this.form.get('config.target').valueChanges;
		accessToken
			.pipe(
				takeUntil(this._unsubscribe),
				debounceTime(1000),
				tap(() => {
					this._setSmartSheetUrl();
				})
			)
			.subscribe();
		target
			.pipe(
				takeUntil(this._unsubscribe),
				debounceTime(1000),
				tap(() => {
					this._setSmartSheetUrl();
				})
			)
			.subscribe();
	}

	private _setFormData(dataSource?: PublicDataSource) {
		this.form.patchValue(dataSource);

		this.context.clear();
		if (dataSource?.config?.context?.length) {
			for (const context of dataSource.config.context) {
				this.context.push(this._createContext({ context }));
			}
		}

		this.mapping.clear();
		if (dataSource?.config?.mapping?.length) {
			for (const { path, displayName, format } of dataSource.config.mapping) {
				this.mapping.push(this._createMapping({ path, displayName, format }));
			}
		}

		if (dataSource?.config?.params) {
			this.form.patchValue({ config: { params: JSON.stringify(dataSource.config.params) } });
		}

		this.sortableFields.clear();
		if (dataSource?.config?.sortableFields?.length) {
			for (const { path, displayName } of dataSource.config.sortableFields) {
				this.sortableFields.push(this._createSortableField({ path, displayName }));
			}
		}

		this.filters.clear();
		if (dataSource?.config?.filters?.length) {
			for (const filter of dataSource.config.filters) {
				this.filters.push(this._createFilter(filter));
			}
		}
	}

	private _setSmartSheetUrl() {
		if (!this.data?.dataSource?.slug) {
			if (!this.form.get('config.accessToken').value || !this.form.get('config.target').value) {
				return;
			}
		}

		let reqBody = {
			accessKey: this.form.get('config.accessToken').value,
			targetId: this.form.get('config.target').value,
			slug: this.data?.dataSource?.slug,
			type: DataSourceConnectorType.SmartSheet
		};

		this.dataSourceService.getFromSource(reqBody).subscribe(response => {
			this.form.get('config.sourceLink').setValue(response?.data);
			if (response?.data && this.data?.dataSource?.slug) {
				this.dataSourceService.update(this.data?.dataSource?.slug, { dataSourceLink: response.data }).pipe(take(1)).subscribe();
			}
		});
	}

	private _createMapping({ path, displayName, format }) {
		return this.formBuilder.group({
			path: [path, [Validators.required]],
			displayName: [displayName, [Validators.required]],
			format: [format, [Validators.required]]
		});
	}

	private _createContext({ context }) {
		return this.formBuilder.group({
			context: [context, [Validators.required]]
		});
	}

	private _createSortableField({ path, displayName }) {
		return this.formBuilder.group({
			path: [path, [Validators.required]],
			displayName: [displayName, [Validators.required]]
		});
	}

	private _createFilter(filter: FilterDef) {
		return this.formBuilder.group({
			id: [filter.id, [Validators.required]],
			name: [filter.name, [Validators.required]],
			category: [filter.category, [Validators.required]],
			paramKey: [filter.paramKey, [Validators.required]],
			type: [filter.type, [Validators.required]],
			layout: [filter.layout],
			grow: [filter.grow],
			topPadding: [filter.topPadding],
			options: [filter.options],
			globalSettingsOptionsKey: [filter.globalSettingsOptionsKey],
			value: [filter.value],
			entityName: [filter.entityName],
			matchEndpoint: [filter.matchEndpoint],
			order: [filter.order],
			extra: [filter.extra]
		});
	}

	private startSlugValidation() {
		// enforce the slug input to always have correct formatting
		this.form
			.get('slug')
			.valueChanges.pipe(
				takeUntil(this._unsubscribe),
				tap((slug: string) => {
					if (slug) {
						this.form.patchValue({ slug: stringToSlug(slug) });
					}
				})
			)
			.subscribe();

		// change slug based on name if user is creating a new data source
		this.form
			.get('name')
			.valueChanges.pipe(
				takeUntil(this._unsubscribe),
				tap((name: string) => {
					// if its edit or if user typed into slug field,
					// we don't want to automatically change the slug for the user
					if (this.data?.dataSource?.slug || this.form.get('slug').dirty) {
						return;
					}
					if (name) {
						this.form.patchValue({ slug: stringToSlug(name) });
					}
				})
			)
			.subscribe();
	}

	async onExport() {
		const dataSource = this.data?.dataSource;

		if (!dataSource) {
			return;
		}

		return this.dataSourceService.bulkGetFromUrl(dataSource.id).subscribe({
			next: (data: string) => {
				let date = new Date().getFullYear() + '-' + (new Date().getMonth() + 1) + '-' + new Date().getDate();
				date = date.concat(' at ' + new Date().getHours() + '-' + new Date().getMinutes() + '-' + new Date().getSeconds());
				let filename = `${dataSource.slug}-${date}`;

				const url = this.document.defaultView.URL.createObjectURL(
					new Blob([JSON.stringify(data, null, 2)], { type: 'text/plain' })
				);
				const link = this.document.createElement('a');
				link.href = url;
				link.download = stringToFilename(filename);
				link.click();
				this.document.defaultView.URL.revokeObjectURL(url);
			},
			error: () => {
				this.globalService.triggerErrorMessage(
					undefined,
					$localize`:Error Message@@collectionContainerExportEntityErrorMessageText:There was an error downloading the export.`
				);
			}
		});
	}

	onImport() {
		const dialogSettings: DataImportDialogDataModel = {
			title: $localize`:Import Data Sources Button@@adminDataSourcesImportButton:Import data sources`,
			copy: '',
			showCancelButton: true,
			showDialogCloseButton: true,
			uploadAccept: '.json, .ccedatasource',
			dropAreaTitle: $localize`:Drop Area Title@@dataImportDialogDropAreaTitle:Choose a file or drag it here to replace the uploaded file`,
			dropAreaCopy: $localize`:Drop Area Description@@dataImportDialogDropAreaCopy:File format & any restrictions here`,
			event: ImportEvent.DataSourceImport,
			onCheckFile: async (file: File) => this.prepareImportedFile(file)
		};

		this.dialog
			.open(DataImportDialogComponent, {
				width: '640px',
				panelClass: ['background-color-alternative', 'max-width-lg'],
				data: dialogSettings
			})
			.afterClosed()
			.pipe(first())
			.subscribe((response: any) => {
				if (response && response != 'null') {
					this.openDataShowcaseModal(response);
				}
				if (!response?.length) {
					return;
				}
			});
	}

	private async prepareImportedFile(file: File): Promise<File> {
		let importedItems: PublicDataSource[] = JSON.parse(await file.text());
		const preparedImport = importedItems[0] as PublicDataSource;

		if (this.currentDataSource) {
			preparedImport.slug = this.currentDataSource.slug;
		}

		return new File([new Blob([JSON.stringify([preparedImport], null, 2)])], file.name, {
			type: file.type
		});
	}

	private openDataShowcaseModal(response: any) {
		this.dialog.open(ResponseShowcaseComponent, {
			width: '480px',
			panelClass: ['background-color-alternative', 'max-width-lg'],
			data: response
		});
	}

	updateDatasourceNameLocale($event: string) {
		this.form.patchValue({
			locale: {
				name: $event
			}
		});
	}
}
